import { useState, useEffect } from 'react'
import { BrowserRouter as Router, Routes, Route, Link, useNavigate } from 'react-router-dom'
import './App.css'
import CatalogView from './components/CatalogView'
import AdminIndex from './pages/admin/index'
import UserRegistration from './components/UserRegistration'
import GroupManagement from './components/GroupManagement'
import QRScanHandler from './components/QRScanHandler'
import GroupInviteHandler from './components/GroupInviteHandler'
import OfflineIndicator from './components/OfflineIndicator'
import Impressum from './pages/Impressum'
import Datenschutz from './pages/Datenschutz'
import { processSyncQueue, initSyncService } from './utils/sync'
import { getDB } from './utils/crypto'
import { supabase } from './services/supabase'

function AppContent() {
  const navigate = useNavigate();
  const [userRegistered, setUserRegistered] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // Check if the user is already registered
  useEffect(() => {
    const checkUserRegistration = async () => {
      try {
        // Ensure database is properly initialized with all required stores
        const db = await getDB();
        
        // Check for the user_data store
        const userInfo = await db.get('user_data', 'user_info');
        
        if (userInfo) {
          // User exists in IndexedDB, check if they're still valid in Supabase
          const { data: sessionData } = await supabase.auth.getSession();
          
          if (sessionData.session) {
            // User is signed in to Supabase
            setUserRegistered(true);
          } else {
            // Try to log in with stored credentials if available
            const credentials = await db.get('user_data', 'credentials');
            
            if (credentials) {
              // Sign in with stored credentials
              const { error: signInError } = await supabase.auth.signInWithPassword({
                email: credentials.email,
                password: credentials.password
              });
              
              setUserRegistered(!signInError);
            } else {
              // No stored credentials
              setUserRegistered(false);
            }
          }
        } else {
          // No user in IndexedDB
          setUserRegistered(false);
        }
      } catch (error) {
        console.error('Error checking user registration:', error);
        setUserRegistered(false);
      } finally {
        setIsLoading(false);
      }
    };
    
    checkUserRegistration();
  }, []);
  
  // Initialize sync service and check queue on app start
  useEffect(() => {
    if (userRegistered) {
      // Only initialize sync service when user is registered
      initSyncService();
      
      // Initial sync attempt
      if (navigator.onLine) {
        processSyncQueue().catch(console.error);
      }

      // Set up event listeners for online status changes
      const handleOnline = () => {
        console.log('App is online. Attempting to sync data...');
        processSyncQueue().catch(console.error);
      };

      window.addEventListener('online', handleOnline);

      // Clean up event listeners
      return () => {
        window.removeEventListener('online', handleOnline);
      };
    }
  }, [userRegistered]);

  // Handle completion of registration
  const handleRegistrationComplete = (redirectPath?: string) => {
    setUserRegistered(true);
    // If redirectPath is provided, navigate to it after a short delay to allow state to update
    if (redirectPath) {
      setTimeout(() => {
        navigate(redirectPath);
      }, 100);
    }
  };
  
  // Loading screen
  if (isLoading) {
    return (
      <div className="fancy-loading-screen">
        <div className="loading-content">
          <img 
            src="/images/FGLogoEnteSchriftAus.png" 
            alt="Fachgruppe Logo" 
            className="loading-logo"
          />
          <h1 className="loading-title">Rätseljagd</h1>
          <h2 className="loading-subtitle">TU Braunschweig</h2>
          <div className="loading-spinner">
            <div className="spinner-circle"></div>
            <div className="spinner-circle"></div>
            <div className="spinner-circle"></div>
          </div>
          <p className="loading-text">Wird geladen...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="app-container">
      <OfflineIndicator />
      {!userRegistered ? (
        // Show registration if user is not registered
        <UserRegistration onComplete={handleRegistrationComplete} />
      ) : (
        // Show app content if user is registered
        <Routes>
          <Route path="/" element={<CatalogView />} />
          <Route path="/admin" element={<AdminIndex />} />
          <Route path="/groups" element={<GroupManagement />} />
          <Route path="/scan" element={<QRScanHandler />} />
          <Route path="/invite" element={<GroupInviteHandler />} />
          <Route path="/impressum" element={<Impressum />} />
          <Route path="/datenschutz" element={<Datenschutz />} />
          <Route path="*" element={
            <div className="not-found">
              <h2>Seite nicht gefunden</h2>
              <p>Die angeforderte Seite existiert nicht.</p>
              <Link to="/">Zurück zur Startseite</Link>
            </div>
          } />
        </Routes>
      )}
    </div>
  )
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  )
}

export default App
